import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { Send, Square, ChevronDown, Bot, Paperclip } from '../../icons';
import { AutoGrowTextarea, Chip } from '../shared';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
// 첨부는 채팅·메일·결재가 쓰는 것을 **그대로** 쓴다 — 새로 만들지 않는다.
import { useAttachmentUpload, AttachmentUploader, WorkspaceFilePicker } from '../Attachment';
import { useAuthStore } from '../../store/authStore';
import { filesFromTransfer, transferHasFiles } from '../../lib/transfer-files';
import { AI_STARTER_DOMAINS, type AiStarterDomain } from '../../lib/ai-starters';
import { AI_MODEL_ALIAS_LABEL, type AiModelAlias } from '@workchat/shared-types';
import { useAiChatStore } from '../../store/aiChatStore';

/**
 * 컴포저 — 패널과 AI 메뉴가 **같은 것**을 쓴다 (설계 판 B · 정의서 C1·C7·C8).
 *
 * ── 모델 고르기 — 관문 G2 (2026-09-09) ──────────────────────────────────
 * ★★★ **있을 때만 그린다.** 별칭(빠르게·고르게·깊게)이 어느 모델인지는 **배포가 env 로 정하고**,
 *    안 정해 둔 별칭은 없는 것이다. 서버가 「고를 수 있는 것」을 알려 주고 그게 **비면 안 그린다** —
 *    셋을 늘 세워 놓고 둘이 같은 모델이면 그건 고르는 시늉이다.
 * ★★ 사람이 고르는 단위는 **별칭**이지 모델 이름이 아니다. 그래야 판 E 의 에이전트가
 *    실모델 이름을 저장하지 않는다(저장한 뒤엔 모델이 바뀔 때마다 전부 손봐야 한다).
 *
 * ── 에이전트 — 판 E (2026-09-09) ─────────────────────────────────────────
 * ★★★ **고른 에이전트는 «칩 하나»로 보인다.** 「어떤 역할로 묻고 있는지」가 안 보이면
 *    답이 왜 그렇게 나왔는지 알 수 없다. ×를 누르면 놓는다(그러면 지금까지처럼 그냥 묻는다).
 * ★★ **에이전트의 시작 질문은 «아직 아무 말도 없을 때»만** 뜬다 — 대화가 시작된 뒤에도
 *    남아 있으면 그건 예시가 아니라 잡동사니다. 누르면 도메인 칩과 같게 **글자로 꽂힌다**.
 *
 * ── 첨부 (2026-09-10 사용자 요청) ─────────────────────────────────────────
 * ★★★ **이제 «갈 곳»이 생겨서 그린다.** 예전 머리말은 「+ 첨부는 눌러도 아무 일이 없으므로
 *    세우지 않는다」였다 — 게이트웨이가 `{prompt, roomId}` 만 날라 첨부를 보낼 칸이 없었기
 *    때문이다. 판 F~H 가 서버에 «파일에서 글을 뽑는 것»을 만들었고, 이제 chat-server 가
 *    그 글을 **질문 앞에 실어** 보낸다(`assist-attachments.ts`). 그래서 세운다.
 * ★ 올리는 길은 셋이다 — **클립 단추 · 붙여넣기 · 끌어다 놓기**. 채팅 입력창과 같은 규칙
 *    (`lib/transfer-files.ts`)을 쓴다. 한쪽에서 되는 것이 다른 쪽에서 안 되면 그것부터
 *    결함으로 읽힌다.
 *
 * ── ⚠ 아직 그리지 «않는» 것 ───────────────────────────────────────────────
 * 「웹」·「AxiWorks」 토글. **도구 카탈로그를 가르는 한 쌍**이라 orchestrator 가 그 뜻을 받아
 * 도구를 걸러야 하는데 아직 그 자리가 없다. 눌러도 아무 일이 없는 조작은 세우지 않는다.
 *
 * ── 지키는 것 ─────────────────────────────────────────────────────────────
 * ★★★ **도메인 목록은 body 로 띄운다**(`useAnchoredMenu`). 컴포저는 스크롤되는 상자 «안»에
 *    있어서 제자리에 그리면 잘린다 — 이 저장소에서 네 번 겪은 함정이다(설계 §9-11).
 * ★★★ **Enter 두 번이 두 번 보내지 않게** `ref` 로 막는다. 상태(`streaming`)로만 막으면
 *    같은 프레임 안의 두 번째 키를 못 잡는다 — 결재에서 10초에 문서 8건이 생긴 자리다.
 * ★ 고른 스타터는 **입력창에 글자로 꽂힌다**(C8) — 바로 보내지 않는다. 고쳐 쓸 수 있어야 한다.
 */

/** 팝업 크기 — `useAnchoredMenu` 가 **열기 전에** 넘침을 판정하려면 알아야 한다(CSS 와 함께 고친다). */
const STARTER_MENU = { width: 320, height: 160 };
/** 첨부 고르기 메뉴 — 같은 이유로 크기를 미리 알려 준다. */
const ATTACH_MENU = { width: 232, height: 116 };
/**
 * 한 질문에 붙일 수 있는 파일 수.
 *
 * ★★★ **서버와 같은 값이어야 한다** — chat-server 의 `assist-attachments.ts` 가
 *    `ASSIST_MAX_FILES` 개까지만 읽는다. 화면이 더 받으면 나머지는 **조용히 버려지고**
 *    사용자는 「붙였는데 왜 안 보지」를 겪는다. 그쪽을 고치면 여기도 고친다.
 */
const AI_MAX_FILES = 5;

export function AiComposer({ placeholder = '무엇이든 물어보세요' }: { placeholder?: string }) {
  const [text, setText] = useState('');
  const [openDomain, setOpenDomain] = useState<AiStarterDomain | null>(null);
  const streaming = useAiChatStore((s) => s.streaming);
  const send = useAiChatStore((s) => s.send);
  const stop = useAiChatStore((s) => s.stop);
  const agent = useAiChatStore((s) => s.agent);
  const setAgent = useAiChatStore((s) => s.setAgent);
  const hasMessages = useAiChatStore((s) => s.messages.length > 0);
  const modelAliases = useAiChatStore((s) => s.modelAliases);
  const modelChoices = useAiChatStore((s) => s.modelChoices);
  const choiceKey = useAiChatStore((s) => s.choiceKey);
  const setChoiceKey = useAiChatStore((s) => s.setChoiceKey);
  const model = useAiChatStore((s) => s.model);
  const setModel = useAiChatStore((s) => s.setModel);
  const loadModels = useAiChatStore((s) => s.loadModels);

  useEffect(() => { void loadModels(); }, [loadModels]);

  const menu = useAnchoredMenu<HTMLButtonElement>(STARTER_MENU, { align: 'start' });
  /* ★ 상태가 아니라 ref 다 — 같은 프레임에 두 번 눌려도 두 번째는 여기서 막힌다. */
  const sendingRef = useRef(false);

  /* ── 첨부 (2026-09-10) ─────────────────────────────────────────────────── */
  const attach = useAttachmentUpload({ maxItems: AI_MAX_FILES });
  const attachMenu = useAnchoredMenu<HTMLButtonElement>(ATTACH_MENU);
  const [wsOpen, setWsOpen] = useState(false);
  const myUserId = useAuthStore((st) => (st.user ? String(st.user.id) : null));
  const [dragOver, setDragOver] = useState(false);
  /* 안쪽 자식으로 들어갈 때마다 dragleave 가 뜬다 — 셈으로 눌러야 테두리가 깜빡이지 않는다. */
  const dragDepth = useRef(0);
  const { addFiles } = attach;

  const onPasteFiles = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    const files = filesFromTransfer(e.clipboardData);
    if (files.length === 0) return;
    /* ★ **막지 않는다** — 글과 그림이 함께 실려 오면 글은 브라우저가 넣게 둔다. */
    void addFiles(files);
  }, [addFiles]);

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!transferHasFiles(e.dataTransfer)) return;
    /* ★ 여기서 막지 않으면 drop 이 아예 안 온다(브라우저 규약). */
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!transferHasFiles(e.dataTransfer)) return;
    dragDepth.current += 1;
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }, []);

  const onDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    const files = filesFromTransfer(e.dataTransfer);
    dragDepth.current = 0;
    setDragOver(false);
    if (files.length === 0) return;
    /* ★ 드롭은 **반드시 막는다** — 안 막으면 브라우저가 그 파일로 화면을 옮겨 버린다. */
    e.preventDefault();
    void addFiles(files);
  }, [addFiles]);

  const submit = (): void => {
    const t = text.trim();
    /* ★ 올리는 중에는 안 보낸다 — 보내면 그 파일 없이 물어보게 된다. */
    if (!t || sendingRef.current || streaming || attach.uploadingFile) return;
    sendingRef.current = true;
    send(t, attach.attachments.map((a) => ({ fileId: a.fileId, name: a.name })));
    setText('');
    attach.clearAttachments();
    // 스트리밍이 끝나기를 기다리지 않는다 — 잠금은 `streaming` 이 잇는다.
    queueMicrotask(() => { sendingRef.current = false; });
  };

  return (
    <div className="aicmp">
      {agent && (
        /* ★ `role="list"` 는 `inline` 칩(=`role="listitem"`)의 짝이다 — 바깥 상자가 진다. */
        <div className="aicmp__agent" role="list">
          <Chip
            inline
            variant="primary"
            size="sm"
            lead={<Bot size={12} aria-hidden />}
            onRemove={() => setAgent(null)}
            removeLabel={`${agent.name} 에이전트 해제`}
          >
            {agent.name}
          </Chip>
        </div>
      )}

      {agent && !hasMessages && agent.starters.length > 0 && (
        <div className="aicmp__astarters">
          {agent.starters.map((q) => (
            <button
              key={q}
              type="button"
              className="aicmp__astarter"
              /* 도메인 칩과 같은 규칙 — 꽂기만 하고 바로 안 보낸다(C8). */
              onClick={() => setText(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <div
        className={`aicmp__box${dragOver ? ' is-dragover' : ''}`}
        onPaste={onPasteFiles}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* 끌어다 놓는 동안만 — 어디에 놓으면 되는지 보여 준다. */}
        {dragOver && (
          <div className="aicmp__drop" role="status">
            <Paperclip size={15} aria-hidden />
            <span>여기에 놓으면 첨부됩니다</span>
          </div>
        )}
        {/* 올린 것·올리는 중을 보여 준다. 채팅·메일이 쓰는 그 컴포넌트다. */}
        <AttachmentUploader state={attach} disabled={streaming} />
        <AutoGrowTextarea
          className="aicmp__ta"
          rows={1}
          value={text}
          placeholder={placeholder}
          aria-label="AI 에게 물어보기"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            /* Enter 로 보내고 Shift+Enter 로 줄을 바꾼다. 조합 중(한글)에는 보내지 않는다 —
               안 막으면 마지막 글자가 잘린 채 나간다. */
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
            e.preventDefault();
            submit();
          }}
        />
        <div className="aicmp__row">
          {/* 파일 첨부 — 내 PC 와 워크스페이스 중에서 고른다(채팅 입력창과 같은 두 갈래). */}
          <div className="aicmp__attachwrap">
            <button
              ref={attachMenu.anchorRef}
              type="button"
              className="aicmp__attach"
              onClick={attachMenu.toggle}
              disabled={streaming || !!attach.uploadingFile}
              title="파일 붙이기"
              aria-label="파일 붙이기"
              aria-haspopup="menu"
              aria-expanded={!!attachMenu.pos}
            >
              <Paperclip size={14} aria-hidden />
            </button>
            {/* 메뉴는 body 로 띄운다 — 컴포저는 스크롤되는 상자 «안»이라 제자리에 그리면 잘린다. */}
            {attachMenu.pos && createPortal(
              <>
                <div className="aicmp__scrim" role="presentation" onMouseDown={attachMenu.close} />
                <div
                  className="aicmp__pop"
                  role="menu"
                  aria-label="첨부할 파일 고르기"
                  style={{ top: attachMenu.pos.top, left: attachMenu.pos.left, width: ATTACH_MENU.width }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="aicmp__starter"
                    onClick={() => { attachMenu.close(); attach.handleAttachClick(); }}
                  >
                    내 PC 에서 선택
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="aicmp__starter"
                    onClick={() => { attachMenu.close(); setWsOpen(true); }}
                  >
                    워크스페이스에서 선택
                  </button>
                </div>
              </>,
              document.body,
            )}
          </div>
          {/*
            판 K — **회사가 지정한 연결** 고르기 (사용자 지적: 「대화에서 모델을 선택할 수 없다」).
            ★ 별칭(`AI_MODEL_*`)은 이 배포가 안 정해 두면 빈 목록이라 영영 안 떴다. 회사가 지정한
              연결이 그 자리를 채운다 — 둘은 **다른 축**이라 나란히 둔다.
            ★ 둘 이상일 때만 그린다. 하나뿐이면 고르는 것이 아니다.
          */}
          {modelChoices !== null && modelChoices.length > 1 && (
            <div className="aicmp__models" role="group" aria-label="쓸 모델">
              {modelChoices.map((c) => {
                const on = choiceKey === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    className={`aicmp__model${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    title={`${c.label} 로 답하게 합니다`}
                    /* 다시 누르면 «안 고름» 으로 — 그러면 회사가 정한 기본이 답한다. */
                    onClick={() => setChoiceKey(on ? null : c.key)}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* 고를 수 있는 것이 둘 이상일 때만 그린다 — 하나뿐이면 고르는 것이 아니다. */}
          {modelAliases.length > 1 && (
            <div className="aicmp__models" role="group" aria-label="답변 방식">
              {modelAliases.map((a: AiModelAlias) => {
                const on = model === a;
                return (
                  <button
                    key={a}
                    type="button"
                    className={`aicmp__model${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    title={AI_MODEL_ALIAS_LABEL[a].hint}
                    /* 다시 누르면 «안 고름» 으로 — 그러면 배포의 기본이 답한다. */
                    onClick={() => setModel(on ? null : a)}
                  >
                    {AI_MODEL_ALIAS_LABEL[a].label}
                  </button>
                );
              })}
            </div>
          )}
          {streaming ? (
            <button type="button" className="aicmp__stop" onClick={stop} title="중단" aria-label="중단">
              <Square size={13} aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              className="aicmp__send"
              onClick={submit}
              disabled={!text.trim()}
              title="보내기"
              aria-label="보내기"
            >
              <Send size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* 이미 올려 둔 파일 붙이기 — 채팅·결재가 쓰는 그 화면이다.
          ★ `allowOthers` 는 안 켠다: 서버가 소유자 기준으로 판정하므로 켜면 못 읽을 것을 고르게 된다. */}
      <WorkspaceFilePicker
        open={wsOpen}
        myUserId={myUserId}
        pickedIds={attach.attachments.map((a) => a.fileId)}
        onClose={() => setWsOpen(false)}
        onPick={attach.addExisting}
      />

      {/* 도메인 칩 — 가로로 밀지 않고 **줄을 바꾼다**. 밀면 뒤의 것은 없는 것이 된다(C7). */}
      <div className="aicmp__doms">
        {AI_STARTER_DOMAINS.map((d) => {
          const Icon = d.icon;
          const on = openDomain?.key === d.key && !!menu.pos;
          return (
            <button
              key={d.key}
              type="button"
              className={`aicmp__dom${on ? ' is-on' : ''}`}
              ref={on ? menu.anchorRef : undefined}
              aria-expanded={on}
              aria-haspopup="menu"
              onClick={(e) => {
                // 앵커를 이 버튼으로 옮긴 뒤 연다 — 훅이 그 자리를 재서 body 에 띄운다.
                (menu.anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current =
                  e.currentTarget;
                if (openDomain?.key === d.key && menu.pos) { menu.close(); setOpenDomain(null); return; }
                setOpenDomain(d);
                if (menu.pos) menu.close();
                menu.toggle();
              }}
            >
              <Icon size={13} aria-hidden />
              {d.label}
              <ChevronDown size={12} aria-hidden />
            </button>
          );
        })}
      </div>

      {menu.pos && openDomain && createPortal(
        <>
          {/* 가려진 곳을 눌러도 닫히게 투명한 덮개를 함께 깐다(스크롤·Esc 는 훅이 닫는다). */}
          <div className="aicmp__scrim" onClick={() => { menu.close(); setOpenDomain(null); }} aria-hidden />
          <div
            className="aicmp__pop"
            role="menu"
            aria-label={`${openDomain.label} 시작 질문`}
            style={{ top: menu.pos.top, left: menu.pos.left, width: STARTER_MENU.width }}
          >
            {openDomain.starters.map((s) => (
              <button
                key={s}
                type="button"
                role="menuitem"
                className="aicmp__starter"
                onClick={() => {
                  /* ★ 꽂기만 한다 — 바로 보내지 않는다(C8). 고쳐 쓸 수 있어야 한다. */
                  setText(s);
                  menu.close();
                  setOpenDomain(null);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
